import { useEffect, useState } from 'react';
import {
  BookingStatusEnum,
  LoginMode,
  WebCheckinDisabledStateLogic,
} from '@msc-b2c/interfaces/enums';
import { WebCheckinProps } from '@msc-b2c/interfaces/types';
import { Button, Icon, Text } from '@msc-b2c/shared-components';
import {
  countDaysUntil,
  getActiveSession,
  getEnableWciOnBookingConfirmation,
  useGetBookingQuery,
  useGetCheckinsQuery,
} from '@msc-b2c/shared-utils';
import { skipToken } from '@reduxjs/toolkit/query';
import { getCabinIdOfTheLoggedUser, getDepartureDate } from '../utils/myAreaUtils';

import { getCorrectDescription, removeAemPlaceholder, removeSkeleton } from './helpers/helpers';

import styles from './webcheckin.module.scss';
import { getCheckinStartDate, isActive, isStatusComplete } from '../utils/checkinUtils';

const Webcheckin = (webCheckinProps: WebCheckinProps) => {
  const {
    webCheckinButtonLabel,
    buttonLink,
    webCheckinDescriptionCurrentlyActive,
    webCheckinDescriptionActive,
    webCheckinDescriptionStatusComplete,
    webCheckinDescriptionInactive,
    webCheckinIcon,
    webCheckinStatusCompletedLabel,
    webCheckinStatusIcon,
    webCheckinStatusIconAccesibility,
    webCheckinStatusLabel,
    webCheckinTitle,
    webCheckinDisabledStateLogic,
    dataComponentName,
  } = webCheckinProps;
  const { loginMode = LoginMode.FULL, bookingNumber = '' } = getActiveSession();

  // State to store formatted date when check-in opens
  const [checkinStartDate, setCheckinStartDate] = useState({ day: '', month: '', year: '' });

  // State to store days remaining until departure
  const [daysBeforeDeparture, setDaysBeforeDeparture] = useState(0);

  // State to control component visibility
  const [isHidden, setIsHidden] = useState(true);

  // Get user session data and booking details
  const { data: booking } = useGetBookingQuery(bookingNumber || skipToken);

  const isOpt = booking?.status?.code === BookingStatusEnum.OPTIONAL;
  const cabinId = getCabinIdOfTheLoggedUser(booking) || '';

  const { data: checkInStatus } = useGetCheckinsQuery(
    bookingNumber && cabinId ? { bookingNumber, cabinId } : skipToken
  );

  /**
   * Effect runs when booking data is available,
   * calculates check-in timing and fetches check-in status
   */
  useEffect(() => {
    if (booking && checkInStatus) {
      if (!getEnableWciOnBookingConfirmation()) {
        // Calculate days until cruise departure
        const departureDate = getDepartureDate(booking);
        const days = countDaysUntil(new Date().toISOString(), departureDate);
        setDaysBeforeDeparture(days);

        // Compute when check-in becomes available
        const checkinStart = getCheckinStartDate(departureDate);
        setCheckinStartDate(checkinStart);

        // Set component visibility based on check-in state and configuration
        setIsHidden(
          webCheckinDisabledStateLogic === WebCheckinDisabledStateLogic.HIDE_COMPONENT
            ? !isActive(days) || isStatusComplete(checkInStatus)
            : false
        );
      } else {
        setIsHidden(
          webCheckinDisabledStateLogic === WebCheckinDisabledStateLogic.HIDE_COMPONENT
            ? isStatusComplete(checkInStatus)
            : false
        );
      }
      removeSkeleton();
    }
  }, [booking, bookingNumber, checkInStatus, webCheckinDisabledStateLogic]);

  if (loginMode === LoginMode.LIGHT || !booking || (isOpt && booking)) {
    removeSkeleton();
    if (isOpt) {
      removeAemPlaceholder();
    }
    return null;
  }

  return (
    !isHidden && (
      <div className={styles.webCheckinContainer}>
        <div className={styles.iconContainer}>
          <Icon
            icon={webCheckinIcon}
            ariaLabel={webCheckinStatusIconAccesibility}
          />
        </div>
        <div className={styles.titleContainer}>
          <Text
            styleOption="header-s spacer-mb-010"
            text={webCheckinTitle}
            tagHtml="h3"
          />
        </div>
        <div className={styles.descriptionContainer}>
          <Text
            styleOption="body-big"
            text={getCorrectDescription(
              webCheckinDescriptionCurrentlyActive,
              webCheckinDescriptionActive,
              webCheckinDescriptionInactive,
              webCheckinDescriptionStatusComplete,
              daysBeforeDeparture,
              checkInStatus,
              checkinStartDate
            )}
          />
        </div>
        <div
          className={`${styles.statusAndButtonContainer} ${
            !isStatusComplete(checkInStatus) ? styles.justifyEnd : ''
          }`}
        >
          {isStatusComplete(checkInStatus) && (
            <div className={styles.statusContainer}>
              <Text
                styleOption="body-default statusBold"
                text={webCheckinStatusLabel}
              />
              <div className={styles.statusCompleteContainer}>
                <div className={styles.statusIconContainer}>
                  <Icon
                    icon={webCheckinStatusIcon}
                    ariaLabel={webCheckinStatusIconAccesibility}
                  />
                </div>
                <Text
                  styleOption="body-default"
                  text={webCheckinStatusCompletedLabel}
                />
              </div>
            </div>
          )}
          <div className={styles.buttonContainer}>
            <Button
              id={`button-${dataComponentName}`}
              styleOption="cmp-button__primary -full-width"
              text={webCheckinButtonLabel}
              buttonLink={buttonLink}
              disabled={
                getEnableWciOnBookingConfirmation() ? false : !isActive(daysBeforeDeparture)
              }
            />
          </div>
        </div>
      </div>
    )
  );
};

export default Webcheckin;
